import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  edit_assigne_student_data,
  update_enrolled_course_date,
} from "../../../api/branches/Branch";

const EditAssigneStudent = () => {
  const navigate = useNavigate();
  const { student_id, class_id } = useParams();

  const [editData, setEditData] = useState([]);
  const [termSelectedUnitsByCourse, setTermSelectedUnitsByCourse] = useState({});
  const [expandedTerms, setExpandedTerms] = useState({});
  const [activeCourseId, setActiveCourseId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load existing enrollment data
  useEffect(() => {
    async function loadData() {
      const data = await edit_assigne_student_data(student_id);
      const courses = data?.data || [];
      setEditData(courses);
      if (courses.length > 0) {
        setActiveCourseId(courses[0].course_id);
      }
    }
    loadData();
  }, [student_id]);

  // Initialize selected units from existing data
  useEffect(() => {
    const initialized = {};
    editData.forEach((course) => {
      initialized[course.course_id] = {};
      course.terms.forEach((term, termIndex) => {
        initialized[course.course_id][termIndex] = {};
        term.subjects.forEach((subject, subjectIndex) => {
          initialized[course.course_id][termIndex][subjectIndex] = subject.units
            .filter((unit) => unit.status === 1)
            .map((unit) => unit.unit_id);
        });
      });
    });
    setTermSelectedUnitsByCourse(initialized);
  }, [editData]);

  const toggleTerm = (key) => {
    setExpandedTerms((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleLectureInputChange = (e, courseId, termIndex, subjectIndex, unitId) => {
    setTermSelectedUnitsByCourse((prev) => {
      const updated = { ...prev };
      if (!updated[courseId]) updated[courseId] = {};
      if (!updated[courseId][termIndex]) updated[courseId][termIndex] = {};
      if (!updated[courseId][termIndex][subjectIndex]) updated[courseId][termIndex][subjectIndex] = [];

      const isSelected = updated[courseId][termIndex][subjectIndex].includes(unitId);
      if (isSelected) {
        updated[courseId][termIndex][subjectIndex] = updated[courseId][termIndex][subjectIndex].filter((id) => id !== unitId);
      } else {
        updated[courseId][termIndex][subjectIndex].push(unitId);
      }
      if (updated[courseId][termIndex][subjectIndex].length === 0) delete updated[courseId][termIndex][subjectIndex];
      if (Object.keys(updated[courseId][termIndex] || {}).length === 0) delete updated[courseId][termIndex];
      if (Object.keys(updated[courseId] || {}).length === 0) delete updated[courseId];
      return updated;
    });
  };

  const handleTermCheckboxChange = (courseId, termIndex) => {
    setTermSelectedUnitsByCourse((prev) => {
      const updated = { ...prev };
      const course = editData.find((c) => c.course_id === courseId);
      if (!course) return updated;
      const term = course.terms[termIndex];
      if (!term) return updated;

      if (!updated[courseId]) updated[courseId] = {};
      if (!updated[courseId][termIndex]) updated[courseId][termIndex] = {};

      const allSelected = term.subjects.every((subject, si) =>
        subject.units.every((unit) => updated[courseId]?.[termIndex]?.[si]?.includes(unit.unit_id))
      );

      if (allSelected) {
        delete updated[courseId][termIndex];
      } else {
        term.subjects.forEach((subject, si) => {
          updated[courseId][termIndex][si] = subject.units.map((u) => u.unit_id);
        });
      }
      if (Object.keys(updated[courseId][termIndex] || {}).length === 0) delete updated[courseId][termIndex];
      if (Object.keys(updated[courseId] || {}).length === 0) delete updated[courseId];
      return updated;
    });
  };

  const onSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const submissionData = [];
    editData.forEach((course) => {
      course.terms.forEach((term, termIndex) => {
        const termUnits = termSelectedUnitsByCourse[course.course_id]?.[termIndex];
        if (termUnits) {
          term.subjects.forEach((subject, subjectIndex) => {
            const selectedUnits = termUnits[subjectIndex];
            if (selectedUnits?.length) {
              selectedUnits.forEach((unitId) => {
                submissionData.push({
                  course_id: course.course_id,
                  termName: term.term_name,
                  subject_id: subject.subject_id,
                  unit_id: unitId,
                });
              });
            }
          });
        }
      });
    });
    const payload = { student_id, courses: submissionData };
    const result = await update_enrolled_course_date(payload);
    if (result?.status === true) {
      navigate("/branch/class-management/all");
    }
    setIsSubmitting(false);
  };

  const activeCourse = editData.find((c) => c.course_id === activeCourseId);

  const s = {
    page: { padding: "20px 24px" },
    header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 },
    title: { fontSize: 18, fontWeight: 600, margin: 0, color: "#1a1a2e" },
    backBtn: {
      width: 32, height: 32, borderRadius: "50%", border: "1px solid #ddd",
      display: "flex", alignItems: "center", justifyContent: "center",
      color: "#555", textDecoration: "none", fontSize: 13, background: "#fff",
    },
    courseTabs: { display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" },
    courseTab: (active) => ({
      padding: "8px 16px", borderRadius: 6, fontSize: 13, fontWeight: 500,
      border: active ? "1px solid #3b82f6" : "1px solid #ddd",
      background: active ? "#eff6ff" : "#fff",
      color: active ? "#1d4ed8" : "#555",
      cursor: "pointer", transition: "all 0.15s", userSelect: "none",
    }),
    termCard: { border: "1px solid #e8e8e8", borderRadius: 8, marginBottom: 12, overflow: "hidden" },
    termHeader: {
      display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
      background: "#f8f9fa", borderBottom: "1px solid #e8e8e8", cursor: "pointer", userSelect: "none",
    },
    termCheckbox: { width: 16, height: 16, cursor: "pointer", accentColor: "#3b82f6", flexShrink: 0 },
    termName: { fontSize: 14, fontWeight: 600, color: "#333", flex: 1 },
    termToggle: { fontSize: 11, color: "#999" },
    termBody: { padding: "8px 14px 12px" },
    subjectRow: (isLast) => ({
      display: "flex", alignItems: "flex-start", gap: 12, padding: "8px 0",
      borderBottom: isLast ? "none" : "1px solid #f0f0f0",
    }),
    subjectName: { width: 180, flexShrink: 0, fontSize: 13, fontWeight: 500, color: "#444", paddingTop: 4 },
    unitsWrap: { display: "flex", flexWrap: "wrap", gap: 6, flex: 1 },
    unitChip: (checked) => ({
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "4px 10px", borderRadius: 16, fontSize: 12,
      cursor: "pointer", userSelect: "none", transition: "all 0.15s",
      border: checked ? "1px solid #3b82f6" : "1px solid #ddd",
      background: checked ? "#eff6ff" : "#fff",
      color: checked ? "#1d4ed8" : "#666",
      fontWeight: checked ? 500 : 400,
    }),
    unitCheckbox: { width: 13, height: 13, cursor: "pointer", accentColor: "#3b82f6" },
    saveBar: { display: "flex", justifyContent: "flex-end", padding: "16px 0", marginTop: 4 },
    saveBtn: {
      padding: "10px 32px", background: "#3b82f6", color: "#fff",
      border: "none", borderRadius: 6, fontSize: 14, fontWeight: 500, cursor: "pointer",
    },
    saveBtnDisabled: {
      padding: "10px 32px", background: "#94a3b8", color: "#fff",
      border: "none", borderRadius: 6, fontSize: 14, fontWeight: 500, cursor: "not-allowed",
    },
    divider: { border: "none", borderTop: "1px solid #eee", margin: "0 0 16px 0" },
  };

  return (
    <div className="right-nav" id="dash-event" style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <h4 style={s.title}>Update Enrollment</h4>
        <Link to="/branch/class-management/all" style={s.backBtn}>
          <i className="fa fa-arrow-left"></i>
        </Link>
      </div>

      {/* Course Tabs */}
      {editData.length > 0 && (
        <div style={s.courseTabs}>
          {editData.map((course) => (
            <div
              key={course.course_id}
              style={s.courseTab(activeCourseId === course.course_id)}
              onClick={() => setActiveCourseId(course.course_id)}
            >
              {course.course_name}
            </div>
          ))}
        </div>
      )}

      <hr style={s.divider} />

      {/* Edit existing course */}
      {activeCourse && (
        <>
          {activeCourse.terms.map((term, termIndex) => {
            const key = `${activeCourse.course_id}-${termIndex}`;
            const isTermSelected = term.subjects.every((subject, si) =>
              subject.units.every((unit) =>
                termSelectedUnitsByCourse[activeCourse.course_id]?.[termIndex]?.[si]?.includes(unit.unit_id)
              )
            );
            const isExpanded = expandedTerms[key] === true;
            return (
              <div key={key} style={s.termCard}>
                <div style={s.termHeader} onClick={() => toggleTerm(key)}>
                  <input
                    type="checkbox" style={s.termCheckbox}
                    checked={isTermSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => handleTermCheckboxChange(activeCourse.course_id, termIndex)}
                  />
                  <span style={s.termName}>Term {term.term_name}</span>
                  <span style={s.termToggle}>
                    <i className={`fa fa-chevron-${isExpanded ? "up" : "down"}`}></i>
                  </span>
                </div>
                {isExpanded && (
                  <div style={s.termBody}>
                    {term.subjects.map((subjectObj, subjectIndex) => (
                      <div key={subjectIndex} style={s.subjectRow(subjectIndex === term.subjects.length - 1)}>
                        <div style={s.subjectName}>{subjectObj.subject_name}</div>
                        <div style={s.unitsWrap}>
                          {subjectObj.units.map((unit) => {
                            const checked = termSelectedUnitsByCourse[activeCourse.course_id]?.[termIndex]?.[subjectIndex]?.includes(unit.unit_id) || false;
                            return (
                              <label key={unit.unit_id} style={s.unitChip(checked)}>
                                <input
                                  type="checkbox" style={s.unitCheckbox}
                                  checked={checked}
                                  onChange={(e) => handleLectureInputChange(e, activeCourse.course_id, termIndex, subjectIndex, unit.unit_id)}
                                />
                                Unit {unit.unit_id}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          <div style={s.saveBar}>
            <button
              style={isSubmitting ? s.saveBtnDisabled : s.saveBtn}
              disabled={isSubmitting} onClick={onSubmit}
            >
              {isSubmitting ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default EditAssigneStudent;
